import { useState } from 'react';
import { Link } from 'react-router-dom';

export default function HomePage() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');

  // Handle scroll for navbar
  useState(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormStatus('submitting');

    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('email', formData.email);
      formBody.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d51umvra2jsd7vukg070', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setFormStatus('success');
        setFormData({ name: '', email: '', message: '' });
        setTimeout(() => setFormStatus('idle'), 5000);
      } else {
        setFormStatus('error');
        setTimeout(() => setFormStatus('idle'), 5000);
      }
    } catch (error) {
      setFormStatus('error');
      setTimeout(() => setFormStatus('idle'), 5000);
    }
  };

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setIsMenuOpen(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-md' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <div className="flex items-center">
              <img 
                src="https://public.readdy.ai/ai/img_res/0b00a2cc-a8fc-49d6-9cc7-41a7aae6a481.png" 
                alt="SEGURIDAD S.R.L." 
                className="h-12 w-auto"
              />
            </div>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-8">
              <button onClick={() => scrollToSection('home')} className={`text-sm font-medium transition-colors hover:text-blue-600 ${isScrolled ? 'text-gray-900' : 'text-white'}`}>
                Home
              </button>
              <button onClick={() => scrollToSection('vpn-overview')} className={`text-sm font-medium transition-colors hover:text-blue-600 ${isScrolled ? 'text-gray-900' : 'text-white'}`}>
                VPN Overview
              </button>
              <button onClick={() => scrollToSection('features')} className={`text-sm font-medium transition-colors hover:text-blue-600 ${isScrolled ? 'text-gray-900' : 'text-white'}`}>
                Features
              </button>
              <button onClick={() => scrollToSection('contact')} className={`text-sm font-medium transition-colors hover:text-blue-600 ${isScrolled ? 'text-gray-900' : 'text-white'}`}>
                Contact
              </button>
              <button onClick={() => scrollToSection('support')} className={`text-sm font-medium transition-colors hover:text-blue-600 ${isScrolled ? 'text-gray-900' : 'text-white'}`}>
                Support
              </button>
            </div>

            {/* Mobile Menu Button */}
            <button 
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="md:hidden p-2"
            >
              <i className={`ri-menu-line text-2xl ${isScrolled ? 'text-gray-900' : 'text-white'}`}></i>
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="md:hidden bg-white border-t">
            <div className="px-6 py-4 space-y-3">
              <button onClick={() => scrollToSection('home')} className="block w-full text-left text-sm font-medium text-gray-900 hover:text-blue-600 py-2">
                Home
              </button>
              <button onClick={() => scrollToSection('vpn-overview')} className="block w-full text-left text-sm font-medium text-gray-900 hover:text-blue-600 py-2">
                VPN Overview
              </button>
              <button onClick={() => scrollToSection('features')} className="block w-full text-left text-sm font-medium text-gray-900 hover:text-blue-600 py-2">
                Features
              </button>
              <button onClick={() => scrollToSection('contact')} className="block w-full text-left text-sm font-medium text-gray-900 hover:text-blue-600 py-2">
                Contact
              </button>
              <button onClick={() => scrollToSection('support')} className="block w-full text-left text-sm font-medium text-gray-900 hover:text-blue-600 py-2">
                Support
              </button>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section id="home" className="relative min-h-screen flex items-center overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img 
            src="https://public.readdy.ai/ai/img_res/93ff7dc087113e144037b8573ba4899e.jpg"
            alt="VPN Security Background"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/60"></div>
        </div>

        {/* Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 w-full py-32">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            {/* Left Column */}
            <div className="lg:col-span-5">
              <div className="inline-block px-4 py-2 bg-blue-600/20 backdrop-blur-sm rounded-full mb-6">
                <span className="text-xs font-semibold text-blue-300 uppercase tracking-wider">Enterprise VPN Solution</span>
              </div>
              <h1 className="text-5xl lg:text-7xl font-bold text-white mb-6 leading-tight">
                Secure Your Digital Identity
              </h1>
              <p className="text-lg lg:text-xl text-gray-300 mb-8 leading-relaxed">
                Military-grade encryption protecting your privacy across all devices and networks
              </p>
              <button 
                onClick={() => scrollToSection('vpn-overview')}
                className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-blue-600 to-cyan-500 text-white font-semibold rounded-full hover:shadow-lg hover:shadow-blue-500/50 transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                Start Protection
                <i className="ri-arrow-right-line ml-2 text-xl"></i>
              </button>
            </div>

            {/* Center Column - Visual */}
            <div className="lg:col-span-4 flex justify-center">
              <div className="relative">
                <img 
                  src="https://readdy.ai/api/search-image?query=3D%20rendered%20VPN%20shield%20icon%20with%20glowing%20blue%20force%20field%20protection%20bubble%2C%20metallic%20silver%20and%20electric%20blue%20colors%2C%20floating%20on%20clean%20white%20background%20with%20soft%20drop%20shadow%2C%20modern%20minimalist%20security%20emblem%20representing%20digital%20privacy%20and%20encryption%20technology&width=600&height=600&seq=shield-visual-001&orientation=squarish"
                  alt="VPN Protection Shield"
                  className="w-80 h-80 object-contain rounded-3xl"
                />
              </div>
            </div>

            {/* Right Column - Feature Cards */}
            <div className="lg:col-span-3 space-y-4">
              <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-cyan-400 rounded-xl flex items-center justify-center mb-3">
                  <i className="ri-shield-check-line text-white text-2xl"></i>
                </div>
                <h3 className="text-white font-semibold mb-1">256-bit Encryption</h3>
                <p className="text-gray-300 text-sm">Bank-level security</p>
              </div>
              <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20">
                <div className="w-12 h-12 bg-gradient-to-br from-cyan-500 to-blue-400 rounded-xl flex items-center justify-center mb-3">
                  <i className="ri-global-line text-white text-2xl"></i>
                </div>
                <h3 className="text-white font-semibold mb-1">Global Network</h3>
                <p className="text-gray-300 text-sm">Worldwide coverage</p>
              </div>
              <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20">
                <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-blue-400 rounded-xl flex items-center justify-center mb-3">
                  <i className="ri-eye-off-line text-white text-2xl"></i>
                </div>
                <h3 className="text-white font-semibold mb-1">Zero Logs</h3>
                <p className="text-gray-300 text-sm">Complete privacy</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* VPN Overview Section */}
      <section id="vpn-overview" className="py-32 bg-white relative overflow-hidden">
        {/* Background Watermark */}
        <div className="absolute bottom-0 right-0 text-gray-50 text-[200px] font-bold leading-none opacity-30 select-none">
          VPN
        </div>

        <div className="max-w-7xl mx-auto px-6 lg:px-12 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Left Content */}
            <div>
              <div className="inline-block px-4 py-2 bg-cyan-50 rounded-full mb-6">
                <span className="text-xs font-semibold text-cyan-600 uppercase tracking-wider">What is VPN</span>
              </div>
              <h2 className="text-4xl lg:text-6xl font-bold text-gray-900 mb-8 leading-tight">
                Your Private Tunnel Through The Internet
              </h2>
              <div className="space-y-6 text-lg text-gray-600 leading-relaxed">
                <p>
                  A Virtual Private Network (VPN) creates a secure, encrypted connection between your device and the internet. Think of it as a private tunnel that shields your online activities from prying eyes, hackers, and data collectors.
                </p>
                <p>
                  When you connect to SEGURIDAD VPN, your internet traffic is routed through our secure servers, masking your IP address and encrypting all data. This ensures that your browsing history, personal information, and online communications remain completely private.
                </p>
                <p>
                  Whether you're working remotely, accessing public Wi-Fi, or simply browsing the web, our VPN technology provides enterprise-grade security that protects your digital identity at all times.
                </p>
              </div>
              <button className="mt-8 inline-flex items-center px-6 py-3 border-2 border-gray-900 text-gray-900 font-semibold rounded-full hover:bg-gray-900 hover:text-white transition-all duration-300 whitespace-nowrap cursor-pointer">
                Learn More
                <i className="ri-arrow-right-line ml-2"></i>
              </button>
            </div>

            {/* Right Visual */}
            <div className="relative">
              <div className="relative rounded-[40px] overflow-hidden shadow-2xl">
                <img 
                  src="https://readdy.ai/api/search-image?query=Isometric%20illustration%20of%20encrypted%20data%20flowing%20through%20secure%20VPN%20tunnel%20with%20glowing%20blue%20and%20cyan%20light%20trails%2C%20abstract%20geometric%20shapes%20representing%20network%20nodes%20and%20privacy%20protection%2C%20modern%20tech%20visualization%20with%20soft%20gradients%20and%20clean%20minimalist%20design%20on%20light%20background&width=800&height=900&seq=vpn-flow-001&orientation=portrait"
                  alt="VPN Data Flow"
                  className="w-full h-auto"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-32 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-20">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              Protection Features
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Advanced security features designed to keep your data safe and your identity private
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Feature 1 - Military Encryption */}
            <div className="group relative h-[520px] rounded-3xl overflow-hidden cursor-pointer">
              <img 
                src="https://readdy.ai/api/search-image?query=Deep%20blue%20server%20room%20with%20glowing%20encryption%20symbols%20and%20secure%20data%20locks%2C%20military-grade%20cybersecurity%20visualization%20with%20geometric%20patterns%20and%20digital%20shields%2C%20futuristic%20technology%20atmosphere%20with%20blue%20and%20cyan%20lighting%20effects%20on%20dark%20background&width=600&height=800&seq=feature-encryption-001&orientation=portrait"
                alt="Military Encryption"
                className="w-full h-full object-cover group-hover:brightness-110 transition-all duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent"></div>
              <div className="absolute bottom-0 left-0 right-0 p-12">
                <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mb-4">
                  <i className="ri-lock-password-line text-white text-3xl"></i>
                </div>
                <h3 className="text-3xl font-bold text-white mb-4">Military Encryption</h3>
                <p className="text-gray-300 text-base leading-relaxed mb-4">
                  AES 256-bit encryption ensures your data is protected with the same security standards used by governments and financial institutions worldwide.
                </p>
                <a href="#" className="inline-flex items-center text-blue-400 font-medium hover:text-blue-300 transition-colors cursor-pointer">
                  Explore
                  <i className="ri-arrow-right-line ml-2"></i>
                </a>
              </div>
            </div>

            {/* Feature 2 - Global Network */}
            <div className="group relative h-[520px] rounded-3xl overflow-hidden cursor-pointer">
              <img 
                src="https://readdy.ai/api/search-image?query=Green-tinted%20global%20server%20network%20visualization%20with%20interconnected%20nodes%20and%20fiber%20optic%20cables%2C%20world%20map%20with%20glowing%20connection%20points%2C%20futuristic%20data%20center%20infrastructure%20with%20emerald%20and%20teal%20lighting%20on%20dark%20technological%20background&width=600&height=800&seq=feature-network-001&orientation=portrait"
                alt="Global Network"
                className="w-full h-full object-cover group-hover:brightness-110 transition-all duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent"></div>
              <div className="absolute bottom-0 left-0 right-0 p-12">
                <div className="w-16 h-16 bg-emerald-600 rounded-full flex items-center justify-center mb-4">
                  <i className="ri-earth-line text-white text-3xl"></i>
                </div>
                <h3 className="text-3xl font-bold text-white mb-4">Global Network</h3>
                <p className="text-gray-300 text-base leading-relaxed mb-4">
                  Access our worldwide network of secure servers strategically located across continents to ensure fast, reliable connections wherever you are.
                </p>
                <a href="#" className="inline-flex items-center text-emerald-400 font-medium hover:text-emerald-300 transition-colors cursor-pointer">
                  Explore
                  <i className="ri-arrow-right-line ml-2"></i>
                </a>
              </div>
            </div>

            {/* Feature 3 - Zero Logs Policy */}
            <div className="group relative h-[520px] rounded-3xl overflow-hidden cursor-pointer">
              <img 
                src="https://readdy.ai/api/search-image?query=Abstract%20purple%20and%20violet%20data%20flow%20visualization%20with%20encrypted%20particles%20dissolving%20into%20digital%20void%2C%20privacy%20protection%20concept%20with%20flowing%20streams%20of%20secure%20information%2C%20futuristic%20minimalist%20design%20with%20purple%20tones%20and%20dark%20atmospheric%20background&width=600&height=800&seq=feature-privacy-001&orientation=portrait"
                alt="Zero Logs Policy"
                className="w-full h-full object-cover group-hover:brightness-110 transition-all duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent"></div>
              <div className="absolute bottom-0 left-0 right-0 p-12">
                <div className="w-16 h-16 bg-purple-600 rounded-full flex items-center justify-center mb-4">
                  <i className="ri-file-shield-line text-white text-3xl"></i>
                </div>
                <h3 className="text-3xl font-bold text-white mb-4">Zero Logs Policy</h3>
                <p className="text-gray-300 text-base leading-relaxed mb-4">
                  We don't track, collect, or store any of your online activities. Your privacy is absolute, with no records of your browsing history or connection data.
                </p>
                <a href="#" className="inline-flex items-center text-purple-400 font-medium hover:text-purple-300 transition-colors cursor-pointer">
                  Explore
                  <i className="ri-arrow-right-line ml-2"></i>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-32 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 relative overflow-hidden">
        {/* Diagonal Light Beam */}
        <div className="absolute top-0 right-0 w-full h-full opacity-5">
          <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-bl from-white to-transparent transform rotate-12"></div>
        </div>

        <div className="max-w-7xl mx-auto px-6 lg:px-12 relative z-10">
          <div className="mb-16">
            <h2 className="text-4xl lg:text-6xl font-bold text-white mb-4">Get In Touch</h2>
            <p className="text-lg text-white/70">Our team responds within 24 hours</p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            {/* Left Column - Contact Form */}
            <div className="lg:col-span-5">
              <div className="bg-white rounded-[32px] p-16 shadow-2xl">
                <form onSubmit={handleSubmit} data-readdy-form id="contact-form">
                  <div className="mb-8">
                    <label className="block text-xs font-semibold text-blue-600 uppercase tracking-wider mb-2">
                      Name
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      required
                      className="w-full text-lg text-gray-900 border-b-2 border-gray-300 focus:border-blue-600 outline-none pb-2 transition-colors"
                      placeholder="Your full name"
                    />
                  </div>

                  <div className="mb-8">
                    <label className="block text-xs font-semibold text-blue-600 uppercase tracking-wider mb-2">
                      Email
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      required
                      className="w-full text-lg text-gray-900 border-b-2 border-gray-300 focus:border-blue-600 outline-none pb-2 transition-colors"
                      placeholder="your@email.com"
                    />
                  </div>

                  <div className="mb-8">
                    <label className="block text-xs font-semibold text-blue-600 uppercase tracking-wider mb-2">
                      Message
                    </label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={(e) => setFormData({...formData, message: e.target.value})}
                      required
                      maxLength={500}
                      rows={4}
                      className="w-full text-lg text-gray-900 border-b-2 border-gray-300 focus:border-blue-600 outline-none pb-2 transition-colors resize-none"
                      placeholder="How can we help you?"
                    ></textarea>
                    <p className="text-xs text-gray-500 mt-1">{formData.message.length}/500 characters</p>
                  </div>

                  <button
                    type="submit"
                    disabled={formStatus === 'submitting'}
                    className="w-full h-14 bg-gradient-to-r from-blue-600 to-cyan-500 text-white font-semibold rounded-full hover:shadow-lg hover:shadow-blue-500/50 transition-all duration-300 whitespace-nowrap cursor-pointer disabled:opacity-50"
                  >
                    {formStatus === 'submitting' ? 'Sending...' : formStatus === 'success' ? 'Message Sent!' : formStatus === 'error' ? 'Error - Try Again' : 'Send Message'}
                  </button>
                </form>
              </div>
            </div>

            {/* Right Column - Contact Information */}
            <div className="lg:col-span-7 space-y-5">
              {/* Email Card */}
              <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-3xl p-10">
                <div className="flex items-start space-x-6">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-cyan-400 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-mail-line text-white text-2xl"></i>
                  </div>
                  <div>
                    <p className="text-sm text-white/70 mb-2">Email Support</p>
                    <a href="mailto:support@seguridad-srl.com" className="text-xl font-bold text-white hover:text-blue-400 transition-colors cursor-pointer">
                      support@seguridad-srl.com
                    </a>
                  </div>
                </div>
              </div>

              {/* Phone Card */}
              <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-3xl p-10">
                <div className="flex items-start space-x-6">
                  <div className="w-12 h-12 bg-gradient-to-br from-cyan-500 to-blue-400 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-phone-line text-white text-2xl"></i>
                  </div>
                  <div>
                    <p className="text-sm text-white/70 mb-2">Direct Line</p>
                    <a href="tel:+18125563648" className="text-xl font-bold text-white hover:text-cyan-400 transition-colors cursor-pointer">
                      +1 8125563648
                    </a>
                  </div>
                </div>
              </div>

              {/* Address Card */}
              <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-3xl p-10">
                <div className="flex items-start space-x-6">
                  <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-blue-400 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-map-pin-line text-white text-2xl"></i>
                  </div>
                  <div>
                    <p className="text-sm text-white/70 mb-2">Headquarters</p>
                    <div className="text-xl font-bold text-white space-y-1">
                      <p>Av. Peron 3391</p>
                      <p>Rosario, S2003FYB</p>
                      <p>Argentina</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Support Section */}
      <section id="support" className="py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-20">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              Support Center
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              We're here to help you get the most out of your VPN experience
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {/* Support Card 1 */}
            <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-2xl p-8 hover:shadow-xl transition-shadow duration-300">
              <div className="w-14 h-14 bg-blue-600 rounded-xl flex items-center justify-center mb-6">
                <i className="ri-question-answer-line text-white text-2xl"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">FAQ</h3>
              <p className="text-gray-600 mb-6 leading-relaxed">
                Find quick answers to common questions about setup, features, and troubleshooting.
              </p>
              <a href="#" className="inline-flex items-center text-blue-600 font-semibold hover:text-blue-700 transition-colors cursor-pointer">
                Browse FAQ
                <i className="ri-arrow-right-line ml-2"></i>
              </a>
            </div>

            {/* Support Card 2 */}
            <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-2xl p-8 hover:shadow-xl transition-shadow duration-300">
              <div className="w-14 h-14 bg-emerald-600 rounded-xl flex items-center justify-center mb-6">
                <i className="ri-book-open-line text-white text-2xl"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Documentation</h3>
              <p className="text-gray-600 mb-6 leading-relaxed">
                Comprehensive guides and tutorials to help you maximize your security and privacy.
              </p>
              <a href="#" className="inline-flex items-center text-emerald-600 font-semibold hover:text-emerald-700 transition-colors cursor-pointer">
                Read Docs
                <i className="ri-arrow-right-line ml-2"></i>
              </a>
            </div>

            {/* Support Card 3 */}
            <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl p-8 hover:shadow-xl transition-shadow duration-300">
              <div className="w-14 h-14 bg-purple-600 rounded-xl flex items-center justify-center mb-6">
                <i className="ri-customer-service-line text-white text-2xl"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Live Chat</h3>
              <p className="text-gray-600 mb-6 leading-relaxed">
                Connect with our support team in real-time for immediate assistance with any issues.
              </p>
              <a href="#" className="inline-flex items-center text-purple-600 font-semibold hover:text-purple-700 transition-colors cursor-pointer">
                Start Chat
                <i className="ri-arrow-right-line ml-2"></i>
              </a>
            </div>

            {/* Support Card 4 */}
            <div className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-2xl p-8 hover:shadow-xl transition-shadow duration-300">
              <div className="w-14 h-14 bg-orange-600 rounded-xl flex items-center justify-center mb-6">
                <i className="ri-mail-send-line text-white text-2xl"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Email Support</h3>
              <p className="text-gray-600 mb-6 leading-relaxed">
                Send us a detailed message and we'll respond within 24 hours with a solution.
              </p>
              <a href="mailto:support@seguridad-srl.com" className="inline-flex items-center text-orange-600 font-semibold hover:text-orange-700 transition-colors cursor-pointer">
                Send Email
                <i className="ri-arrow-right-line ml-2"></i>
              </a>
            </div>
          </div>

          {/* Additional Support Info */}
          <div className="mt-20 bg-gradient-to-r from-slate-900 to-slate-800 rounded-3xl p-12 text-center">
            <h3 className="text-3xl font-bold text-white mb-4">Need Immediate Help?</h3>
            <p className="text-lg text-gray-300 mb-8 max-w-2xl mx-auto">
              Our dedicated support team is available to assist you with any technical issues or questions about your VPN service.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a 
                href="tel:+18125563648"
                className="inline-flex items-center justify-center px-8 py-4 bg-white text-gray-900 font-semibold rounded-full hover:bg-gray-100 transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-phone-line mr-2 text-xl"></i>
                Call Us Now
              </a>
              <button 
                onClick={() => scrollToSection('contact')}
                className="inline-flex items-center justify-center px-8 py-4 border-2 border-white text-white font-semibold rounded-full hover:bg-white hover:text-gray-900 transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-message-3-line mr-2 text-xl"></i>
                Contact Form
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-emerald-900 relative overflow-hidden">
        {/* Upper Section */}
        <div className="relative z-10 border-b border-emerald-800">
          <div className="max-w-7xl mx-auto px-6 lg:px-12 py-16">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
              {/* Brand Column */}
              <div>
                <img 
                  src="https://public.readdy.ai/ai/img_res/0b00a2cc-a8fc-49d6-9cc7-41a7aae6a481.png" 
                  alt="SEGURIDAD S.R.L." 
                  className="h-10 w-auto mb-4"
                />
                <p className="text-emerald-200 text-sm leading-relaxed">
                  Enterprise-grade<br />
                  VPN security<br />
                  for everyone
                </p>
              </div>

              {/* Newsletter Column */}
              <div>
                <h4 className="text-xs font-semibold text-white uppercase tracking-widest mb-4">
                  Stay Updated
                </h4>
                <form className="relative" onSubmit={(e) => e.preventDefault()}>
                  <input
                    type="email"
                    placeholder="Your email"
                    className="w-full bg-transparent border-b-2 border-emerald-600 text-white placeholder-emerald-400 pb-2 pr-10 outline-none focus:border-emerald-400 transition-colors text-sm"
                  />
                  <button type="submit" className="absolute right-0 bottom-2 text-emerald-400 hover:text-emerald-300 transition-colors cursor-pointer">
                    <i className="ri-arrow-right-line text-xl"></i>
                  </button>
                </form>
                <p className="text-xs text-emerald-300 mt-3">
                  We respect your <a href="#" className="underline hover:text-white transition-colors cursor-pointer">privacy</a>
                </p>
              </div>

              {/* Quick Links Column */}
              <div>
                <h4 className="text-xs font-semibold text-white uppercase tracking-widest mb-4">
                  Navigation
                </h4>
                <ul className="space-y-3">
                  <li>
                    <button onClick={() => scrollToSection('home')} className="text-white hover:text-emerald-300 transition-colors text-sm cursor-pointer">
                      Home
                    </button>
                  </li>
                  <li>
                    <button onClick={() => scrollToSection('vpn-overview')} className="text-white hover:text-emerald-300 transition-colors text-sm cursor-pointer">
                      VPN Overview
                    </button>
                  </li>
                  <li>
                    <button onClick={() => scrollToSection('features')} className="text-white hover:text-emerald-300 transition-colors text-sm cursor-pointer">
                      Features
                    </button>
                  </li>
                  <li>
                    <button onClick={() => scrollToSection('contact')} className="text-white hover:text-emerald-300 transition-colors text-sm cursor-pointer">
                      Contact
                    </button>
                  </li>
                </ul>
              </div>

              {/* Legal Column */}
              <div>
                <h4 className="text-xs font-semibold text-white uppercase tracking-widest mb-4">
                  Legal
                </h4>
                <ul className="space-y-3">
                  <li>
                    <a href="#" className="text-white hover:text-emerald-300 transition-colors text-sm cursor-pointer">
                      Privacy Policy
                    </a>
                  </li>
                  <li>
                    <a href="#" className="text-white hover:text-emerald-300 transition-colors text-sm cursor-pointer">
                      Terms of Service
                    </a>
                  </li>
                  <li>
                    <a href="#" className="text-white hover:text-emerald-300 transition-colors text-sm cursor-pointer">
                      Security
                    </a>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Lower Section - Large Typography */}
        <div className="relative py-20">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <div className="text-center">
              <h2 className="text-[120px] md:text-[180px] font-black text-emerald-100/40 leading-none tracking-tighter select-none">
                SEGURIDAD
              </h2>
            </div>
          </div>
        </div>

        {/* Copyright */}
        <div className="border-t border-emerald-800">
          <div className="max-w-7xl mx-auto px-6 lg:px-12 py-6">
            <div className="flex flex-col md:flex-row justify-between items-center text-sm text-emerald-300">
              <p>&copy; 2025 SEGURIDAD S.R.L. All rights reserved.</p>
              <a 
                href="https://readdy.ai/?ref=logo" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-emerald-300 hover:text-white transition-colors mt-2 md:mt-0 cursor-pointer"
              >
                Powered by Readdy
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
